import React, { useState } from 'react';
import Modal from './Modal';
import { api } from '../../config/api';

interface QuestionarioFornecedorModalProps {
  isOpen: boolean;
  onClose: () => void;
  usuarioId: string;
  usuarioNome: string;
  onSuccess: () => void;
}

const QuestionarioFornecedorModal: React.FC<QuestionarioFornecedorModalProps> = ({
  isOpen,
  onClose,
  usuarioNome,
  onSuccess
}) => {
  const [respostas, setRespostas] = useState({
    ja_e_fornecedor: false,
    realizou_venda: false,
    tem_carga_agendar: false,
    realizou_visita_presencial: false,
    deseja_visita: false,
    negocio_fechado: false,
    meio_comunicacao: '',
    observacoes: ''
  });
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const toggleCheckbox = (campo: keyof typeof respostas) => {
    setRespostas(prev => ({ ...prev, [campo]: !prev[campo] }));
  };

  const handleSubmit = async () => {
    setErro('');
    setEnviando(true);

    try {
      await api.post('/questionario-fornecedor', respostas);
      onSuccess();
    } catch (error: any) {
      const msg = error?.message || 'Erro ao salvar questionário';
      setErro(msg);
    } finally {
      setEnviando(false);
    }
  };

  const perguntas: { key: keyof typeof respostas; label: string }[] = [
    { key: 'ja_e_fornecedor', label: 'Você já é nosso fornecedor?' },
    { key: 'realizou_venda', label: 'Conseguiu realizar alguma venda para o grupo Aleplast?' },
    { key: 'tem_carga_agendar', label: 'Tem alguma carga para agendar para a Logística?' },
    { key: 'realizou_visita_presencial', label: 'Já realizou alguma visita presencial aos nossos compradores?' },
    { key: 'deseja_visita', label: 'Deseja realizar visita?' },
    { key: 'negocio_fechado', label: 'O negócio foi fechado?' }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="📋 Questionário de Primeiro Acesso"
      size="lg"
    >
      <div className="space-y-5">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            Olá <strong>{usuarioNome}</strong>, seja bem-vindo! Antes de continuar, precisamos que você responda
            algumas perguntas rápidas para personalizar sua experiência no sistema.
          </p>
        </div>

        <div className="space-y-3">
          {perguntas.map((pergunta) => (
            <label
              key={pergunta.key}
              className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <input
                type="checkbox"
                checked={respostas[pergunta.key] as boolean}
                onChange={() => toggleCheckbox(pergunta.key)}
                className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{pergunta.label}</span>
            </label>
          ))}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Qual meio de comunicação você entrou em contato conosco?
          </label>
          <input
            type="text"
            value={respostas.meio_comunicacao}
            onChange={(e) => setRespostas(prev => ({ ...prev, meio_comunicacao: e.target.value }))}
            placeholder="Ex: WhatsApp, telefone, email, visita presencial..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Observações (opcional)
          </label>
          <textarea
            value={respostas.observacoes}
            onChange={(e) => setRespostas(prev => ({ ...prev, observacoes: e.target.value }))}
            placeholder="Alguma informação adicional que gostaria de compartilhar..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {erro && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">{erro}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={handleSubmit}
            disabled={enviando}
            className="px-6 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {enviando ? 'Enviando...' : 'Enviar Respostas'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default QuestionarioFornecedorModal;
